import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';

import { ErrorService, NotificationService, UserService } from '../../shared/services';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { NotificationTypeEnum } from '../../shared/enums';
import { ApiError, User } from '../../shared/models';
import { TokenUtilities } from './token.utilities';

export interface TokenInterceptorDeps {
  authService: AuthService;
  userService: UserService;
  notificationService: NotificationService;
  errorService: ErrorService;
}

interface InterceptorData {
  request: HttpRequest<any>;
  next: HttpHandlerFn;
  requestToken?: string;
  currentToken?: string;
  error?: HttpErrorResponse;
}

export function sendRequest(
  deps: TokenInterceptorDeps,
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> {
  const requestToken: string = deps.authService.getAccessToken();
  const requestClone: HttpRequest<any> = TokenUtilities.getRequestWithAuthHeader(
    requestToken,
    request,
  );

  if (requestClone) {
    return next(requestClone).pipe(
      catchError((error: HttpErrorResponse) => {
        return catchErrorOnSendRequest(deps, {
          request,
          next,
          requestToken,
          error,
        });
      }),
    );
  }

  // No access token to attach. Only force a logout when the user actually had
  // a session (a refresh token survives) — that's the "lost token mid-session"
  // case the doLogout handles. An anonymous visitor (no refresh token) has no
  // session to terminate; logging them out would navigate them away from
  // public pages (e.g. /:lang/ukrainian-talent), so swallow the request.
  if (!deps.authService.getRefreshToken()) {
    return of(null);
  }

  return doLogout(deps);
}

function catchErrorOnSendRequest(
  deps: TokenInterceptorDeps,
  data: InterceptorData,
): Observable<HttpEvent<any>> {
  const { request, next, requestToken, error } = data;
  const is401or403Error: boolean = TokenUtilities.is401or403Error(error);

  if (is401or403Error) {
    return handleAuthRequestError(deps, { request, next, requestToken });
  } else if (!TokenUtilities.isItRequestListWithoutErrorMessages(request.url, error.status)) {
    handleNoAuthError(deps, request.url, error);
  }

  return throwError(() => error);
}

function handleAuthRequestError(
  deps: TokenInterceptorDeps,
  data: InterceptorData,
): Observable<HttpEvent<any>> {
  const currentToken = deps.authService.getAccessToken();

  if (currentToken) {
    return handleAuthRequestErrorIfTokenExist(deps, { ...data, currentToken });
  }

  return doLogout(deps);
}

function handleAuthRequestErrorIfTokenExist(
  deps: TokenInterceptorDeps,
  data: InterceptorData,
): Observable<HttpEvent<any>> {
  const { request, next, requestToken, currentToken } = data;

  if (requestToken === currentToken) {
    if (deps.authService.getIsAccessTokenRefreshing()) {
      return holdRequest(deps, request, next);
    }

    return doRefresh(deps, { request, next, requestToken: currentToken });
  }

  return handleRequestOnFinish(deps, request, next);
}

function handleRequestOnFinish(
  deps: TokenInterceptorDeps,
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> {
  const token: string = deps.authService.getAccessToken();
  const requestClone: HttpRequest<any> = TokenUtilities.getRequestWithAuthHeader(token, request);

  return next(requestClone).pipe(
    catchError((error: HttpErrorResponse) => handleRequestOnFinishError(deps, request.url, error)),
  );
}

function handleRequestOnFinishError(
  deps: TokenInterceptorDeps,
  requestUrl: string,
  error: HttpErrorResponse,
): Observable<HttpEvent<any>> {
  const is401or403Error: boolean = TokenUtilities.is401or403Error(error);

  if (is401or403Error) {
    return doLogout(deps);
  } else if (TokenUtilities.isItRequestListWithoutErrorMessages(requestUrl, error.status)) {
    return throwError(() => error);
  }

  handleNoAuthError(deps, requestUrl, error);

  return throwError(() => error);
}

function doRefresh(deps: TokenInterceptorDeps, data: InterceptorData): Observable<HttpEvent<any>> {
  const { request, next, requestToken } = data;

  deps.authService.startTokenRefreshing();

  return deps.userService.refresh().pipe(
    catchError(() => {
      deps.authService.stopTokenRefreshing();

      if (requestToken === deps.authService.getAccessToken()) {
        return doLogout(deps);
      }

      return of(null);
    }),
    switchMap((refreshData: HttpResponse<User>) => {
      if (refreshData) {
        deps.authService.handleDataAfterRefresh(refreshData);
        deps.authService.stopTokenRefreshing();
      }

      return handleRequestOnFinish(deps, request, next);
    }),
    finalize(() => getRefreshFinalize(deps)),
  );
}

function getRefreshFinalize(deps: TokenInterceptorDeps): void {
  deps.authService.stopTokenRefreshing();
  deps.authService.handleHeldRequests();
}

function holdRequest(
  deps: TokenInterceptorDeps,
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> {
  const subject = new BehaviorSubject<boolean>(false);
  const subject$ = subject.asObservable();

  deps.authService.pushItemAtRequestsQueue(subject);

  return subject$.pipe(
    filter((handle: boolean) => !!handle),
    switchMap(() => {
      const token: string = deps.authService.getAccessToken();
      const requestClone: HttpRequest<any> = TokenUtilities.getRequestWithAuthHeader(
        token,
        request,
      );

      return next(requestClone);
    }),
  );
}

export function interceptRequestWithoutAuthHeader(
  deps: TokenInterceptorDeps,
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> {
  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      handleNoAuthError(deps, request.url, error);

      return throwError(() => error);
    }),
  );
}

function doLogout(deps: TokenInterceptorDeps): Observable<never> {
  clearRefreshingProcess(deps);
  deps.authService.fullUserLogout(true);

  return EMPTY;
}

function clearRefreshingProcess(deps: TokenInterceptorDeps): void {
  deps.authService.stopTokenRefreshing();
  deps.authService.resetRequestsQueue();
}

// After this throwError is returned
function handleNoAuthError(
  deps: TokenInterceptorDeps,
  requestUrl: string,
  error: HttpErrorResponse,
): void {
  if (!deps.authService.isOnline()) {
    deps.notificationService.notify(
      NotificationTypeEnum.ERROR,
      'NOTIFICATION.NO_INTERNET_CONNECTION',
    );

    return;
  }

  const handledError: HttpErrorResponse = TokenUtilities.getHandledHttpError(error);
  const errorMessage = handledError?.error?.message || 'NOTIFICATION.TRY_AGAIN_LATER';
  const showError: boolean = getShowErrorMessageCondition(
    deps,
    requestUrl,
    errorMessage,
    error.error?.httpStatusCode,
  );

  if (showError) {
    deps.notificationService.notify(NotificationTypeEnum.ERROR, errorMessage);
    deps.errorService.setLastErrorMessage(errorMessage);
  }
}

export function interceptRefreshingRequest(
  deps: TokenInterceptorDeps,
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> {
  const refreshToken: string = deps.authService.getRefreshToken();

  if (refreshToken) {
    const requestClone: HttpRequest<any> = TokenUtilities.getRequestWithAuthHeader(
      refreshToken,
      request,
    );

    return next(requestClone);
  } else {
    return doLogout(deps);
  }
}

function getShowErrorMessageCondition(
  deps: TokenInterceptorDeps,
  requestUrl: string,
  errorMessage: string,
  httpStatusCode: number,
): boolean {
  const lastErrorMessage: string = deps.errorService.getLastErrorMessage();
  const notShowMessage: boolean =
    TokenUtilities.notShowErrorRequestList().some((url: string) => requestUrl.includes(url)) ||
    TokenUtilities.getNotShowMessageInfoList().some(
      (info: ApiError) => errorMessage === info.message && httpStatusCode === info.httpStatusCode,
    );

  return (
    !notShowMessage &&
    !(lastErrorMessage === 'NOTIFICATION.TRY_AGAIN_LATER' && lastErrorMessage === errorMessage)
  );
}
